import { postPreExchangeOperation } from "../../../app/api/exchangeOffer";
import { getBooking } from "../../../app/api/getBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { _eventBus } from "../../../core/events/EventBus";
import { determinePassengerType, isActionRunSuccessfully } from "../../../Lib/Utils/validationUtil";
import * as Sentry from "@sentry/nextjs";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IBookedOffer, IBookingResponse, IBookingTypeTrip, IDeterminePassenger, ILeg, INextAndPreviousTrip, IOffer, IPreExchangeOperationRequest, IPreExchangeOperationResponse, ISelectedPassenger } from "../../../Orchestrator/Types/Index";
import { IPreExchangeOperationStartParams } from "../../../Orchestrator/Types/Interface/ActionParams/SearchBookingParams";
import { calculateAlreadyPaidAddonsAction } from "../Addons/CalculateAlreadyPaidAddons";
import { preSelectAddonsForExchangeAction } from "../Addons/PreSelectAddonsForExchangeAction";
import { prepareBookingSummaryAction } from "../Checkout/Summary/PrepareBookingSummaryAction";
import { getAllOfferWithoutExchangeAction } from "../CommonActions/GetAllOfferWithoutExchangeAction";
import { getAllTripWithoutExchangeAction } from "../CommonActions/GetAllTripWithoutExchangeAction";

class PreExchangeOperationStartAction extends BaseAction<string, IPreExchangeOperationStartParams> {

    override async run(): Promise<string> {

        let response: string = "fail"
        try {
            const { nextAndPreviousTrain, type, nextTrainTicket, trip, exchangeOfferPassengerRefs } = this.params!
            if (nextAndPreviousTrain) {
                const exChangeTrip: INextAndPreviousTrip | null =
                    type === "next"
                        ? nextAndPreviousTrain.nextTrain
                        : nextAndPreviousTrain.previousTrain;
                if (exChangeTrip) {
                    const offerIdsRaw = exChangeTrip.exchangeOfferId ? (Array.isArray(exChangeTrip.exchangeOfferId) ? exChangeTrip.exchangeOfferId : [exChangeTrip.exchangeOfferId]) : [];
                    const offerIds = Array.from(new Set((offerIdsRaw as (string | string[])[]).flatMap(id => Array.isArray(id) ? id : [id])));
                    updateRecoilState<string[]>("exchangeOffersAtom", offerIds)

                    // ERM infant–adult linking: exchange-operations must reference
                    // the WHOLE linked group — lap-infant refs stay with their adult
                    // (the backend rejects group-incomplete operations).
                    let exchangeOffersEntries: Array<{offerId: string, placeSelections: null, passengerRefs: string[], throughTicketTags: null}>;

                    const perOfferRefs = exchangeOfferPassengerRefs ?? exChangeTrip.exchangeOfferPassengerRefs;
                    if (perOfferRefs && perOfferRefs.length > 0) {
                        // Per-pax INDIVIDUAL offers: use each offer's own passengerRefs
                        exchangeOffersEntries = perOfferRefs
                            .map(({ offerId, passengerRefs }) => ({
                                offerId,
                                placeSelections: null,
                                passengerRefs,
                                throughTicketTags: null
                            }))
                            .filter(entry => entry.passengerRefs.length > 0);
                        // Fallback: if all per-offer refs were empty (POOL mode with no
                        // passengerRefs set), revert to scatter mode so the API receives
                        // a valid non-empty payload.
                        if (exchangeOffersEntries.length === 0 && nextTrainTicket.passengers.length > 0) {
                            console.warn(
                                "[PreExchangeOperationStartAction] All per-offer passengerRefs were empty — falling back to scatter mode"
                            );
                            exchangeOffersEntries = offerIds.map((offerId) => ({
                                offerId,
                                placeSelections: null,
                                passengerRefs: nextTrainTicket.passengers.map(d => d.externalRef),
                                throughTicketTags: null
                            }));
                        }
                    } else {
                        // Fallback: one entry per offer ID, all passengers incl. lap infants
                        exchangeOffersEntries = offerIds.map((offerId) => ({
                            offerId,
                            placeSelections: null,
                            passengerRefs: nextTrainTicket.passengers.map(d => d.externalRef),
                            throughTicketTags: null
                        }));
                    }

                    const payloadPreExchangeOperation: IPreExchangeOperationRequest = {
                        exchangeOffers: exchangeOffersEntries
                    }
                    const preExchangeResponse: any = await postPreExchangeOperation(payloadPreExchangeOperation, nextTrainTicket.bookingId)
                    if (preExchangeResponse.status === 'ok' && preExchangeResponse.answer) {
                        const bookingResponse: IPreExchangeOperationResponse = preExchangeResponse.answer;
                        if (bookingResponse.exchangeOperation) {
                            const exchangeOperationId = bookingResponse.exchangeOperation.id;
                            sessionStorage.setItem("exchangeOperationId", exchangeOperationId)
                            sessionStorage.setItem("exchangeBookingId", nextTrainTicket.bookingId)
                            //Get Booking by Booking Id
                            const getBookingResponse: any = await getBooking(nextTrainTicket.bookingId);
                            if (getBookingResponse.status === 'ok' && getBookingResponse.answer) {
                                const bookingResponse: IBookingResponse = getBookingResponse.answer;
                                // Update Recoil state with the booking response
                                updateRecoilState('quickExchangeBookingResponseAtom', bookingResponse);
                                updateRecoilState('bookingResponseAtom', bookingResponse);

                                const passengerList: ISelectedPassenger[] = bookingResponse.booking?.passengers.map(passenger => {
                                    const passengerObject: ISelectedPassenger = {
                                        id: passenger.id,
                                        firstName: passenger?.detail?.firstName || "",
                                        lastName: passenger?.detail?.lastName || "",
                                        email: passenger?.detail?.email || "",
                                        mobile: "",
                                        remember: false,
                                        age: passenger.age,
                                        seatingDetails: null, //uniquesPassengerDetails.find(d => d.passengerId === passenger.id)
                                        cards: passenger.cards || [],
                                        prmNeeds: passenger.prmNeeds
                                    }
                                    return passengerObject
                                });

                                const passengersObject: { [key: string]: number } = {};
                                passengerList.forEach((passenger) => {
                                    const determinePassenger: IDeterminePassenger = {
                                        age: passenger?.age,
                                        cards: passenger?.cards || [],
                                        prmNeeds: ((passenger?.prmNeeds || []).length > 0 && passenger?.prmNeeds) ? passenger?.prmNeeds[0] : ""
                                    }
                                    const passengerType = determinePassengerType(determinePassenger);

                                    // Initialize the count for the passenger type if not already present
                                    if (!passengersObject[passengerType]) {
                                        passengersObject[passengerType] = 0;
                                    }

                                    // Increment the count for this passenger type
                                    passengersObject[passengerType] += 1;
                                });

                                updateRecoilState("searchStateAtom", { passengers: passengersObject });

                                // Carry the original booking's addons over to the new exchange
                                // offer so the user doesn't lose what they paid for. Same
                                // pattern as the CDT sibling (StartPreExchangeOperationAction).
                                // No passenger-cancel filter applies in quick-exchange — the
                                // whole booking is exchanged, so every booked passenger keeps
                                // their addons. Failures inside are non-fatal; the summary
                                // path still runs against the booking snapshot we have.
                                preSelectAddonsForExchangeAction.setParams({
                                    exchangeOperationId,
                                    selectedPassengerForCancel: [],
                                    selectedPassengerForCancelInBound: [],
                                    // Hand in the booking we just refreshed so the action
                                    // doesn't read a stale atom (a previous test session's
                                    // PREBOOKED offer can otherwise leak through and the
                                    // addon gets attached to the wrong bookedOffer).
                                    bookingOverride: bookingResponse,
                                });
                                await preSelectAddonsForExchangeAction.execute();
                                // preSelectAddonsForExchangeAction refreshes bookingResponseAtom
                                // after a successful POST so the new ancillaries are reflected.
                                // Use that refreshed snapshot for the summary path so
                                // otherDetails.addons carries over the existing extras.
                                const refreshedBookingResponse =
                                    getRecoilStateValue<IBookingResponse>("bookingResponseAtom") || bookingResponse;
                                const bookingForSummary = refreshedBookingResponse.booking;

                                /**
                                 * Manage Addons into booking for quick exchange.
                                 * Checking that existing booking have addons or not.
                                 * If have addons then we need to prepare payment summary for already paid addons amount.
                                 * That already paid addons amount will be show into payment summary section.
                                */
                                let allBookedOfferWithoutExchange: IBookedOffer[] = []
                                let allBookedTripsWithoutExchange: IBookingTypeTrip[] = []
                                let changeTripAlreadyPaidAddons = {}
                                getAllOfferWithoutExchangeAction.setParams(bookingForSummary.bookedOffers);
                                getAllOfferWithoutExchangeAction.execute();
                                if (isActionRunSuccessfully(getAllOfferWithoutExchangeAction)) {
                                    allBookedOfferWithoutExchange = getAllOfferWithoutExchangeAction?.result || []
                                }

                                getAllTripWithoutExchangeAction.setParams({ bookedOffers: allBookedOfferWithoutExchange, trips: bookingForSummary.trips });
                                getAllTripWithoutExchangeAction.execute();
                                if (isActionRunSuccessfully(getAllTripWithoutExchangeAction)) {
                                    allBookedTripsWithoutExchange = getAllTripWithoutExchangeAction?.result || []
                                }

                                calculateAlreadyPaidAddonsAction.setParams({
                                    allBookedOfferWithoutExchange: allBookedOfferWithoutExchange,
                                    allBookedTripWithoutExchange: allBookedTripsWithoutExchange
                                })
                                await calculateAlreadyPaidAddonsAction.execute();
                                updateRecoilState("quickExchangeTripDirectionAtom", "")
                                if (isActionRunSuccessfully(calculateAlreadyPaidAddonsAction)) {
                                    const result = calculateAlreadyPaidAddonsAction?.result
                                    if (result?.data) {                                        
                                        const tripId = trip?.tripIds || []
                                        if (tripId.length > 0) {
                                            const tripDirection = ((trip?.tripSummaries || []).find(d => d.id === tripId[0])?.direction || "")?.toLocaleLowerCase()
                                            if (tripDirection != "") {
                                                changeTripAlreadyPaidAddons = {
                                                    [tripDirection]: result.data[tripDirection]
                                                }
                                                updateRecoilState("quickExchangeTripDirectionAtom", tripDirection)
                                            }
                                        }
                                    }
                                }

                                // Set isQuickExchangeStateAtom BEFORE the summary
                                // runs — PrepareBookingSummaryAction reads it to
                                // gate the per-leg `max(0, tickets+addons-credit)+fees`
                                // formula. If we set it after, the summary uses the
                                // legacy total path → `payment.total` ends up 0
                                // even when an exchangeFee is owed, and
                                // /api/payment/session 500s with ZERO_PAYMENT_ERROR.
                                updateRecoilState("isQuickExchangeStateAtom", true)

                                prepareBookingSummaryAction.setParams({
                                    ...bookingForSummary,
                                    isFrom: "quick-exchange",
                                    changeTripAlreadyPaidAddons
                                });
                                await prepareBookingSummaryAction.execute()

                                updateRecoilState("paymentStateAtom", {
                                    // Left unset so <Payment> can preselect the customer's default saved
                                    // card — it only preselects while no method is set (Payment.tsx).
                                    method: null,
                                    total: nextTrainTicket.outboundConfirmedPrice,
                                });

                                response = "success"
                                _eventBus.emit({
                                    eventName: "ROUTE_CHANGE",
                                    data: "/checkout"
                                })
                            }
                        }
                    }
                }
            }

        } catch (error: any) {
            console.error("Error in PreExchangeOperationStartAction:", error);
            Sentry.captureException(error, { tags: { action: "PreExchangeOperationStartAction" } });
        } finally {
            return response;
        }
    }
}

export const preExchangeOperationStartAction = new PreExchangeOperationStartAction()


